import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  useApproveOrgMember,
  useGetOrganizationPendingMembers,
  useRejectOrgMember
} from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import * as toast from 'legacy/common/utils/toast';
import { Avatar } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';

interface Props {
  organizationId: string;
  search: string;
  isAdmin: boolean;
}

const RequestsList = ({ organizationId, search, isAdmin }: Props) => {
  const { t } = useTranslation(['organizations']);
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetOrganizationPendingMembers({
    organizationId,
    search,
    enabled: isAdmin
  });

  const { mutate: approveMemberRequest, isLoading: isApproving } =
    useApproveOrgMember();
  const { mutate: rejectMemberRequest, isLoading: isRejecting } =
    useRejectOrgMember();

  const acceptRequest = (request) => {
    approveMemberRequest(
      {
        groupId: organizationId,
        userId: request.user.id
      },
      {
        onSuccess: () => {
          toast.success(t('members.approveSuccess'));
        },
        onError: () => {
          toast.error(t('members.approveFailed'));
        }
      }
    );
  };

  const rejectRequest = (request) => {
    rejectMemberRequest(
      {
        groupId: organizationId,
        userId: request.user.id
      },
      {
        onSuccess: () => {
          toast.success(t('members.rejectSuccess'));
        },
        onError: () => {
          toast.error(t('members.rejectFailed'));
        }
      }
    );
  };

  if (!isAdmin) {
    return <div className="flex-1" />;
  }

  return (
    <MemberList
      title={t('members.requestsTitle')}
      description={t('members.requestsDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      emptyText={t('members.noRequests')}
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((request) => (
          <MembersListItem
            key={request.id}
            avatar={<Avatar src={request.user.photoUrl} />}
            label={`${request.user.firstName} ${request.user.lastName}`}
            actions={
              isAdmin && (
                <>
                  <button
                    className="text-primary-700 text-sm font-bold"
                    aria-label={t('actions.reject')}
                    type="button"
                    disabled={isApproving || isRejecting}
                    onClick={() => rejectRequest(request)}
                  >
                    {t('actions.reject')}
                  </button>
                  <div className="text-neutral-300">|</div>
                  <button
                    className="text-primary-700 text-sm font-bold"
                    aria-label={t('actions.accept')}
                    type="button"
                    disabled={isApproving || isRejecting}
                    onClick={() => acceptRequest(request)}
                  >
                    {t('actions.accept')}
                  </button>
                </>
              )
            }
          />
        ))
      )}
    </MemberList>
  );
};

export default RequestsList;
